/**
 * Die 3D-Szene mit React Three Fiber.
 * Gebäude und Möbel-Geometrie kommen aus engine/ (prozedural, Three.js),
 * hier wird deklarativ zusammengesetzt, was wann sichtbar ist.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useStore, floorIdx, defFor, currentHouse, furnitureAllowed, measureText } from "../state/store";
import { FLOORS, floorBase } from "../engine/constants";
import { makeParts } from "../engine/furniture";
import { makeLabel, refreshLabel, type LabelMesh } from "../engine/rooms";
import { VARIANTS } from "../engine/variants";
import { buildLine, buildPoint, POINT_INFO } from "../engine/lines";
import type { PlacedItem } from "../engine/placement";
import { Placement } from "../engine/placement";
import { getHouse } from "./house";
import { persp, ortho, walkCam, walk, rig, attachControls, resizeCameras, placeWalkCam } from "./rig";
import { itemObjects, labelMeshes, lineObjects } from "./registry";
import { Interaction } from "./Interaction";

export function Scene() {
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={persp}
      gl={{ antialias: true }}
      onCreated={({ gl }) => { gl.toneMappingExposure = 1.05; }}
      style={{ touchAction: "none" }}
    >
      <Sky />
      <Lights />
      <CameraController />
      <HouseView />
      <Labels />
      <Furniture />
      <Installations />
      <Guides />
      <MeasureView />
      <Interaction />
    </Canvas>
  );
}

/* ---------- Himmel = Farbe aus dem Theme ---------- */
function Sky() {
  const scene = useThree((s) => s.scene);
  useEffect(() => {
    const read = () => { const c = getComputedStyle(document.documentElement).getPropertyValue("--sky").trim() || "#dfe9f0"; scene.background = new THREE.Color(c); };
    read();
    const mq = matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener("change", read);
    const mo = new MutationObserver(read); mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => { mq.removeEventListener("change", read); mo.disconnect(); };
  }, [scene]);
  return null;
}

function Lights() {
  const sun = useRef<THREE.DirectionalLight>(null!);
  const scene = useThree((s) => s.scene);
  useLayoutEffect(() => {
    const l = sun.current;
    l.target.position.set(3.9, 3, -5.5); scene.add(l.target);
    Object.assign(l.shadow.camera, { left: -20, right: 20, top: 20, bottom: -20, near: 1, far: 80 });
    l.shadow.camera.updateProjectionMatrix();
    l.shadow.mapSize.set(2048, 2048); l.shadow.bias = -0.0006; l.shadow.normalBias = 0.02;
    return () => { scene.remove(l.target); };
  }, [scene]);
  return (
    <>
      <hemisphereLight args={[0xf4f7ff, 0x8a7d6a, 1.25]} />
      <ambientLight args={[0xffffff, 0.55]} />
      <directionalLight ref={sun} args={[0xfff3e2, 2.0]} position={[-14, 26, 14]} castShadow />
      <directionalLight args={[0xe8f0ff, 0.45]} position={[18, 10, -24]} />
    </>
  );
}

/* ---------- Kameras, Orbit-Steuerung, Rundgang ---------- */
const WALK_SPOTS: [number, number][] = [[5.3, 3.0], [5.3, 3.8], [5.3, 3.6], [5.3, 3.8]];

function CameraController() {
  const { gl, size, set } = useThree();
  const mode = useStore((s) => s.mode);
  const floor = useStore((s) => s.floor);

  useLayoutEffect(() => {
    for (const c of [persp, ortho, walkCam]) (c as unknown as { manual: boolean }).manual = true; // Größe verwalten wir selbst
    attachControls(gl.domElement);
  }, [gl]);
  useLayoutEffect(() => { resizeCameras(size.width, size.height); }, [size.width, size.height]);

  useLayoutEffect(() => {
    const cam = mode === "orbit" ? persp : mode === "plan" ? ortho : walkCam;
    set({ camera: cam });
    if (rig.orbitP) rig.orbitP.enabled = mode === "orbit";
    if (rig.orbitO) rig.orbitO.enabled = mode === "plan";
  }, [mode, set]);

  useEffect(() => {
    if (mode === "orbit" && rig.orbitP) rig.orbitP.target.y = floor === "all" ? 4 : FLOORS[floor].z + 1;
    if (mode === "walk" && floor !== "all") { walk.f = floor; [walk.x, walk.y] = WALK_SPOTS[floor]; }
  }, [floor, mode]);

  useFrame((_, delta) => {
    const m = useStore.getState().mode;
    if (m === "walk") walkStep(Math.min(0.05, delta));
    else if (m === "orbit") rig.orbitP?.update();
    else rig.orbitO?.update();
  });
  return null;
}

function blocked(x: number, y: number) {
  const r = 0.22;
  for (const c of currentHouse().colliders[walk.f]) if (x > c[0] - r && x < c[2] + r && y > c[1] - r && y < c[3] + r) return true;
  return false;
}
function walkStep(dt: number) {
  const k = walk.keys; let fw = -walk.jy, st = walk.jx;
  if (k["w"] || k["arrowup"]) fw += 1; if (k["s"] || k["arrowdown"]) fw -= 1; if (k["a"]) st -= 1; if (k["d"]) st += 1;
  if (k["arrowleft"]) walk.yaw += dt * 1.8; if (k["arrowright"]) walk.yaw -= dt * 1.8;
  const sp = 1.7 * dt * (k["shift"] ? 1.8 : 1);
  const sy = Math.sin(walk.yaw), cy = Math.cos(walk.yaw);
  const dx = (-sy * fw + cy * st) * sp, dy = (cy * fw + sy * st) * sp;
  if (dx && !blocked(walk.x + dx, walk.y)) walk.x += dx;
  if (dy && !blocked(walk.x, walk.y + dy)) walk.y += dy;
  placeWalkCam();
}

/* ---------- Gebäude ---------- */
function HouseView() {
  const key = useStore((s) => s.houseKey) || "bestand";
  const house = getHouse(key);
  const mode = useStore((s) => s.mode);
  const floor = useStore((s) => s.floor);
  const roof = useStore((s) => s.layers.roof);
  const nb = useStore((s) => s.layers.neighbour);
  const sel = floor === "all" ? null : floor, walking = mode === "walk";
  return (
    <>
      {house.floors.map((g, i) => <primitive key={i} object={g} visible={walking || sel === null || i <= sel} />)}
      <primitive object={house.roof} visible={roof && (walking || sel === null)} />
      <primitive object={house.neighbour} visible={nb && mode !== "plan"} />
      <primitive object={house.site} />
    </>
  );
}

/* ---------- Raumbeschriftung ---------- */
function Labels() {
  const key = useStore((s) => s.houseKey) || "bestand";
  const show = useStore((s) => s.layers.labels);
  const mode = useStore((s) => s.mode);
  const floor = useStore((s) => s.floor);
  const groups = useMemo(() => {
    const gs = FLOORS.map(() => new THREE.Group());
    labelMeshes.length = 0;
    for (const room of VARIANTS[key].rooms) { const m = makeLabel(room, ""); gs[room.f].add(m); labelMeshes.push(m); }
    return gs;
  }, [key]);
  useEffect(() => { // Schrift ist ggf. erst nach dem Laden der Webfonts da
    document.fonts?.ready.then(() => { for (const m of labelMeshes) refreshLabel(m as LabelMesh, ""); });
  }, [groups]);
  return <>{groups.map((g, i) => <primitive key={i} object={g} visible={show && mode !== "walk" && floor === i} />)}</>;
}

/* ---------- Möbel ---------- */
function Furniture() {
  const items = useStore((s) => s.items);
  const show = useStore((s) => s.layers.furniture && furnitureAllowed(s));
  if (!show) return null;
  return <>{items.map((it) => <FurnitureItem key={it.id} item={it} />)}</>;
}

function FurnitureItem({ item }: { item: PlacedItem }) {
  const selected = useStore((s) => s.selectedId === item.id);
  const bad = useStore((s) => s.prefs.coll && s.bad.has(item.id));
  const visible = useStore((s) => s.mode === "walk" || s.floor === "all" || item.f <= (s.floor as number));
  const custom = useStore((s) => (item.k.startsWith("cu:") ? s.custom.find((c) => c.key === item.k) : undefined));
  const groupRef = useRef<THREE.Group>(null!);

  const parts = useMemo(() => {
    const def = item.type === "custom" ? defFor(item.k) : null;
    return makeParts(item.type, item.w, item.d, item.h, item.c, def ? { w: def.w, d: def.d, h: def.h, c: def.c, parts: def.parts || [] } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.type, item.w, item.d, item.h, item.c, item.k, custom]);
  useEffect(() => () => parts.traverse((o) => (o as THREE.Mesh).geometry?.dispose()), [parts]);

  // Auswahl hervorheben (Materialien einmalig klonen, dann Emissive setzen)
  useLayoutEffect(() => {
    parts.traverse((o) => {
      const m = o as THREE.Mesh;
      const mt = m.material as THREE.MeshStandardMaterial | undefined;
      if (m.isMesh && mt && mt.emissive) {
        if (!mt.userData.own) { m.material = mt.clone(); (m.material as THREE.Material).userData.own = true; }
        (m.material as THREE.MeshStandardMaterial).emissive.set(selected ? 0x1d3b5c : 0x000000);
      }
    });
  }, [parts, selected]);

  const outline = useMemo(() => new THREE.EdgesGeometry(new THREE.PlaneGeometry(item.w + 0.04, item.d + 0.04)), [item.w, item.d]);
  useEffect(() => () => outline.dispose(), [outline]);

  useLayoutEffect(() => {
    const g = groupRef.current; g.userData.itemId = item.id; itemObjects.set(item.id, g);
    return () => { itemObjects.delete(item.id); };
  }, [item.id]);

  return (
    <group ref={groupRef} position={[item.x, floorBase(item.f, item.x, item.y) + item.z, -item.y]} rotation={[0, item.r, 0]} visible={visible}>
      <primitive object={parts} />
      <lineSegments geometry={outline} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} renderOrder={9} visible={selected || bad}>
        <lineBasicMaterial color={bad ? 0xc0392b : 0x2f6fae} depthTest={false} />
      </lineSegments>
    </group>
  );
}

/* ---------- Leitungen und Anschlüsse ---------- */
/** sichtbar: im Rundgang das Geschoss, auf dem man steht; sonst gewähltes Geschoss bzw. alle */
function useLineFloorVisible() {
  const mode = useStore((s) => s.mode);
  const floor = useStore((s) => s.floor);
  // im Rundgang ist das gewählte Geschoss das, auf dem man steht
  return useMemo(() => (f: number) => (mode === "walk" ? f === (floor === "all" ? walk.f : floor) : floor === "all" || f === floor), [mode, floor]);
}
function Installations() {
  const lines = useStore((s) => s.lines);
  const points = useStore((s) => s.points);
  const layers = useStore((s) => s.layers);
  const sel = useStore((s) => s.lineSel);
  const draft = useStore((s) => s.draft);
  const draftType = useStore((s) => s.lineOpts.t);
  const draftF = useStore((s) => s.draftF);
  const risers = useStore((s) => s.mode === "plan"); // ↕-Markierung nur im Grundriss, wo senkrechte Stücke sonst unsichtbar sind
  const vis = useLineFloorVisible();
  const group = useMemo(() => {
    const g = new THREE.Group(); lineObjects.clear();
    for (const l of lines) {
      if (!layers[l.t] || !vis(l.f)) continue;
      const o = buildLine(l, sel?.kind === "line" && sel.id === l.id, false, risers); o.userData.lineSel = { kind: "line", id: l.id };
      g.add(o); lineObjects.set(l.id, o);
    }
    for (const p of points) {
      if (!layers[POINT_INFO[p.t].layer] || !vis(p.f)) continue;
      const o = buildPoint(p, sel?.kind === "point" && sel.id === p.id); o.userData.lineSel = { kind: "point", id: p.id };
      g.add(o); lineObjects.set(p.id, o);
    }
    if (draft.length) g.add(buildLine({ t: draftType, f: draftF, pts: draft.length > 1 ? draft : [draft[0], draft[0]] }, true, true, risers));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, points, layers, sel, draft, draftType, draftF, vis, risers]);
  return <primitive object={group} />; // Geometrien und Materialien der Leitungen sind geteilt, nichts zu entsorgen
}

/* ---------- Maßlinien zu Wänden und Möbeln ---------- */
function tagSprite(txt: string, bg: string, w: number, h: number, sx: number, sy: number) {
  const cv = document.createElement("canvas"); cv.width = w; cv.height = h; const g = cv.getContext("2d")!;
  g.fillStyle = bg; g.fillRect(0, 0, w, h); g.fillStyle = "#fff"; g.font = `600 ${Math.round(h * 0.58)}px "IBM Plex Mono",monospace`;
  g.textAlign = "center"; g.textBaseline = "middle"; g.fillText(txt, w / 2, h * 0.53);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false })); sp.scale.set(sx, sy, 1); return sp;
}
function disposeGroup(g: THREE.Group) {
  g.traverse((o) => {
    (o as THREE.Mesh).geometry?.dispose();
    const m = (o as THREE.Mesh).material as THREE.Material & { map?: THREE.Texture };
    if (m && (o as THREE.Sprite).isSprite) { m.map?.dispose(); m.dispose(); }
  });
}
const guideMat = new THREE.LineBasicMaterial({ color: 0xd9822b, depthTest: false });

function Guides() {
  const it = useStore((s) => (s.selectedId ? s.items.find((i) => i.id === s.selectedId) : undefined));
  const items = useStore((s) => s.items);
  const show = useStore((s) => s.prefs.guides);
  const visible = useStore((s) => !!it && (s.mode === "walk" || s.floor === "all" || it.f <= (s.floor as number)));
  const group = useMemo(() => {
    const g = new THREE.Group();
    if (!it || !show || !visible) return g;
    const gaps = new Placement(currentHouse().colliders, items).gaps(it);
    const zb = floorBase(it.f, it.x, it.y) + it.z + 0.04;
    for (const k of ["px", "nx", "py", "ny"] as const) {
      const q = gaps[k]; if (!q || q.d < 0.005) continue;
      const a = new THREE.Vector3(q.from[0], zb, -q.from[1]), b = new THREE.Vector3(q.to[0], zb, -q.to[1]);
      const l = new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]), guideMat); l.renderOrder = 12; g.add(l);
      const sp = tagSprite(Math.round(q.d * 100) + " cm", "#d9822b", 200, 72, 0.5, 0.18); sp.renderOrder = 13;
      sp.position.copy(a).lerp(b, 0.5).setY(zb + 0.14); g.add(sp);
    }
    return g;
  }, [it, items, show, visible]);
  useEffect(() => () => disposeGroup(group), [group]);
  return <primitive object={group} />;
}

/* ---------- Messen ---------- */
const measureMat = new THREE.LineBasicMaterial({ color: 0x2f6fae, depthTest: false });
const dotMat = new THREE.MeshBasicMaterial({ color: 0x2f6fae, depthTest: false });
function MeasureView() {
  const pts = useStore((s) => s.measurePts);
  const walking = useStore((s) => s.mode === "walk");
  const group = useMemo(() => {
    const g = new THREE.Group();
    const v = pts.map((p) => new THREE.Vector3(p[0], p[1] + 0.05, p[2]));
    for (const p of v) { const d = new THREE.Mesh(new THREE.SphereGeometry(0.06, 12, 8), dotMat); d.position.copy(p); d.renderOrder = 10; g.add(d); }
    if (v.length === 2) {
      const l = new THREE.Line(new THREE.BufferGeometry().setFromPoints(v), measureMat); l.renderOrder = 10; g.add(l);
      const txt = measureText(pts[0], pts[1]);
      const w = Math.max(256, txt.length * 34 + 60), sp = tagSprite(txt, "#2f6fae", w, 80, 1.2 * w / 256, 0.375);
      sp.position.copy(v[0]).lerp(v[1], 0.5);
      if (walking) { // im Rundgang immer gleich groß auf dem Bildschirm, sonst wird das Schild riesig
        (sp.material as THREE.SpriteMaterial).sizeAttenuation = false; sp.scale.set(0.075 * w / 80, 0.075, 1);
      } else sp.position.y = Math.max(v[0].y, v[1].y) + 0.3;
      sp.renderOrder = 11; g.add(sp);
    }
    return g;
  }, [pts, walking]);
  useEffect(() => () => disposeGroup(group), [group]);
  return <primitive object={group} />;
}

export { floorIdx };
